import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { userAPI } from '../services/api';
import { getProfilePicture, getUploadUrl, isOnline, formatDate } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { useFriend } from '../context/FriendContext';
import { toast } from 'react-toastify';
import PhotoUploadModal from '../components/PhotoUploadModal';
import Header from '../components/Header';
import {
  FaHeart, FaEye, FaUserFriends, FaMapMarkerAlt, FaInstagram,
  FaFacebook, FaTelegram, FaCamera, FaCog, FaCrown, FaCoins,
  FaGift, FaStar, FaCheckCircle, FaCheck, FaTimes, FaEdit, FaArrowLeft, FaImages,
  FaInfoCircle, FaShieldAlt, FaUser, FaBell, FaSignOutAlt, FaTrash
} from 'react-icons/fa';

const Profile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout: authLogout } = useAuth();
  const { friendRequests, sentFriendRequests, friends: myFriends, acceptFriendRequest, declineFriendRequest, loadFriendRequests } = useFriend();

  // Debug user changes
  useEffect(() => {
    console.log('👤 Profile Header - User changed:', user?.id, user?.profile_photo, user?.full_name);
  }, [user]);

  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Profile: Logout error:', error);
    }
  };
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [normalPhotos, setNormalPhotos] = useState([]);
  const [privatePhotos, setPrivatePhotos] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [profileFriends, setProfileFriends] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [viewerLiked, setViewerLiked] = useState(false);
  const [totalLikes, setTotalLikes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [activeFriendsTab, setActiveFriendsTab] = useState('friends');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const handleDeletePhoto = (photoId, photoType) => {
    setPhotoToDelete({ id: photoId, type: photoType });
    setShowDeleteModal(true);
  };

  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      console.log('Deleting photo:', photoToDelete.id, 'Type:', photoToDelete.type, 'ID type:', typeof photoToDelete.id);
      await userAPI.deletePhoto(photoToDelete.id);

      // Local state'i güncelle - sayfa refresh olmadan
      console.log('Updating local state - removing photo:', photoToDelete.id, 'from type:', photoToDelete.type);
      if (photoToDelete.type === 'normal') {
        setNormalPhotos(prev => {
          const filtered = prev.filter(photo => {
            console.log('Comparing photo.id:', photo.id, 'with photoToDelete.id:', photoToDelete.id, 'types:', typeof photo.id, typeof photoToDelete.id);
            return photo.id !== photoToDelete.id;
          });
          console.log('Normal photos after filter:', filtered.length, 'from', prev.length);
          return filtered;
        });
      } else if (photoToDelete.type === 'private') {
        setPrivatePhotos(prev => {
          const filtered = prev.filter(photo => photo.id !== photoToDelete.id);
          console.log('Private photos after filter:', filtered.length, 'from', prev.length);
          return filtered;
        });
      }

      toast.success('Photo deleted successfully!');
    } catch (error) {
      console.error('Delete photo error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete photo';
      toast.error(errorMessage);

      // Error durumunda da modal'ı kapat ama sayfayı refresh etme
      console.log('Photo delete failed, keeping current state');
    } finally {
      setShowDeleteModal(false);
      setPhotoToDelete(null);
    }
  };

  useEffect(() => {
    console.log('🔍 Profile useEffect triggered:', { id, user: user?.id, hasUser: !!user });

    // If we have a user, try to fetch profile
    // If no user yet, component will re-render when user loads
    if (user && user.id) {
      // If no ID provided in URL, use current user's ID (own profile)
      const profileId = id || user.id;
      console.log('🔍 Using profileId:', profileId, 'from URL id:', id, 'or current user.id:', user.id);

      if (!profileId) {
        console.error('❌ No profileId available!');
        toast.error('Unable to load profile - missing user ID');
        return;
      }

      fetchProfile(profileId);

      // Load friend requests if viewing own profile
      if (profileId === user.id) {
        loadFriendRequests();
      }
    } else {
      console.log('🔍 User not loaded yet, skipping profile fetch');
    }
  }, [id, user]); // Depend on id and user to trigger when either changes

  const fetchProfile = async (profileId) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching profile for user:', profileId);

      const profileResponse = await userAPI.getProfile(profileId);
      console.log('👤 Profile API response:', profileResponse);

      if (!profileResponse?.success) {
        throw new Error('Invalid API response format');
      }

      const profileData = profileResponse.profile;
      const giftsData = profileResponse.gifts || [];
      const friendsData = profileResponse.friends || [];
      const normalPhotosData = profileResponse.profile.normal_photos || [];
      const privatePhotosData = profileResponse.profile.private_photos || [];
      const viewerLikedData = profileResponse.viewer_liked || false;
      const totalLikesData = profileResponse.total_likes || 0;
      // Total views now comes from visitors table (visitors.length)

      console.log('📸 Normal photos from profile:', normalPhotosData.length);
      console.log('🔒 Private photos from profile:', privatePhotosData.length);

      if (!profileData) {
        throw new Error('Profile data not found');
      }

      // Get all photos separately (for fallback if needed)
      let photosData = [];
      try {
        console.log('🔍 Fetching all photos for user:', profileId);
        const photosResponse = await userAPI.getPhotos(profileId);
        console.log('📸 Photos API response:', photosResponse);
        if (photosResponse) {
          photosData = Array.isArray(photosResponse) ? photosResponse : (photosResponse.photos || []);
        }
        console.log('📸 All photos data:', photosData.length);
      } catch (error) {
        console.error('❌ Failed to fetch photos:', error);
      }

      // Get visitors data (optional - don't fail if not available)
      let visitorsData = [];
      try {
        console.log('🔍 Fetching visitors for user:', profileId);
        const visitorsResponse = await userAPI.getVisitors(profileId);
        console.log('👁️ Visitors API response:', visitorsResponse);
        console.log('👁️ Response success:', visitorsResponse?.success);
        if (visitorsResponse && visitorsResponse.success && visitorsResponse.visitors) {
          visitorsData = visitorsResponse.visitors;
          console.log('👁️ Visitors array:', visitorsData.length, 'visitors');
        } else {
          console.log('👁️ No visitors data in response');
        }
      } catch (error) {
        console.error('❌ Failed to fetch visitors (continuing without visitors):', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        // Don't throw error - continue without visitors data
        visitorsData = [];
      }

      console.log('✅ Final photos - Normal:', normalPhotosData.length, 'Private:', privatePhotosData.length, 'All:', photosData.length);

      setProfile(profileData);
      setPhotos(photosData);
      setNormalPhotos(normalPhotosData);
      setPrivatePhotos(privatePhotosData);
      setGifts(giftsData);
      setProfileFriends(friendsData);
      setVisitors(visitorsData);
      setViewerLiked(viewerLikedData);
      setTotalLikes(totalLikesData);
      // Total views is now derived from visitors.length

    } catch (error) {
      console.error('❌ Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const isOwnProfile = user && profile && user.id === profile.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header title="Profile" />

        {/* Main Content */}
        <div className="matchus-main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="matchus-encounters-header" style={{ height: '60px' }}>
          <div className="matchus-header-container" style={{ height: '60px' }}>
            <div className="matchus-header-left">
              <Link to="/encounters" className="matchus-logo">
                <img src="/assets/images/logo-matchus.svg" alt="MatchUs" className="h-6 w-6 mr-2" />
                <span className="matchus-brand-name">Match Us</span>
              </Link>
            </div>

            <div className="matchus-header-center">
              <h1 className="matchus-page-title">My Profile</h1>
            </div>

            <div className="matchus-header-right">
              <button className="matchus-header-btn" title="Notifications">
                <FaBell />
                <span className="matchus-notification-badge">3</span>
              </button>

              <div className="matchus-user-menu">
                <button 
                  className="matchus-user-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <img
                    key={user?.id || 'guest'}
                    src={user?.profile_photo ? getUploadUrl(`user-${user.id}/${user.profile_photo}`) : getProfilePicture(user || { id: 1 })}
                    alt="Profile"
                    className="matchus-user-avatar"
                  />
                  <span className="matchus-user-name">{user?.full_name?.split(' ')[0] || 'You'}</span>
                </button>

                <div className={`matchus-user-dropdown ${showUserDropdown ? 'show' : ''}`}>
                  <Link to="/settings" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <FaCog />
                    <span>Settings</span>
                  </Link>
                  <Link to={`/user/${user?.id}`} className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <FaUser />
                    <span>Profile</span>
                  </Link>
                  <div className="matchus-dropdown-divider"></div>
                  {(user?.role?.name === 'admin' || user?.role?.name === 'moderator') && (
                    <Link to="/admin" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                      <FaCog />
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  <button
                    type="button"
                    className="matchus-dropdown-logout"
                    onClick={() => {
                      setShowUserDropdown(false);
                      logout(() => navigate('/?login'));
                    }}
                  >
                    <FaSignOutAlt />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Overlay to close dropdown */}
        {showUserDropdown && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowUserDropdown(false)}
          />
        )}

        {/* Main Content */}
        <div className="matchus-main-content flex items-center justify-center">
          <div className="text-center">
            <FaUser className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Profile not found</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="matchus-encounters-header" style={{ height: '60px' }}>
        <div className="matchus-header-container" style={{ height: '60px' }}>
          <div className="matchus-header-left">
            <Link to="/encounters" className="matchus-logo">
              <img src="/assets/images/logo-matchus.svg" alt="MatchUs" className="h-6 w-6 mr-2" />
              <span className="matchus-brand-name">Match Us</span>
            </Link>
          </div>

          <div className="matchus-header-center">
            <h1 className="matchus-page-title">My Profile</h1>
          </div>

          <div className="matchus-header-right">
            <button className="matchus-header-btn" title="Notifications">
              <FaBell />
              <span className="matchus-notification-badge">3</span>
            </button>

            <div className="matchus-user-menu">
              <button 
                className="matchus-user-btn"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <img
                  src={user?.profile_photo ? getUploadUrl(`user-${user.id}/${user.profile_photo}`) : getProfilePicture(user || { id: 1 })}
                  alt="Profile"
                  className="matchus-user-avatar"
                />
                <span className="matchus-user-name">{user?.full_name?.split(' ')[0] || 'You'}</span>
              </button>

              <div className={`matchus-user-dropdown ${showUserDropdown ? 'show' : ''}`}>
                <Link to="/settings" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaCog />
                  <span>Settings</span>
                </Link>
                <Link to={`/user/${user?.id}`} className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaUser />
                  <span>Profile</span>
                </Link>
                <div className="matchus-dropdown-divider"></div>
                {(user?.is_admin === 1 || user?.role?.name === 'admin' || user?.role?.name === 'moderator') && (
                  <Link to="/admin" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <FaCog />
                    <span>Admin Panel</span>
                  </Link>
                )}
                <button
                  type="button"
                  className="matchus-dropdown-logout"
                  onClick={() => {
                    setShowUserDropdown(false);
                    logout();
                  }}
                >
                  <FaSignOutAlt />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Overlay to close dropdown */}
      {showUserDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserDropdown(false)}
        />
      )}

      {/* Main Content */}
      <div className="matchus-main-content">
        {/* Cover Photo Section */}
        <div className="relative h-64 md:h-80 bg-gradient-to-r from-pink-500 to-pink-600">

        {/* Settings Button (only for own profile) */}
        {isOwnProfile && (
          <button
            onClick={() => navigate('/settings')}
            className="absolute top-4 right-4 z-10 bg-white/20 backdrop-blur-sm p-2 rounded-full text-white hover:bg-white/30 transition-colors"
          >
            <FaCog className="h-5 w-5" />
          </button>
        )}

        {/* Profile Avatar */}
        <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2">
          <div className="relative group">
            <img
              src={getProfilePicture(profile)}
              alt={profile.full_name}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
            />
            {isOnline(profile.last_seen) && (
              <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-2 border-white rounded-full"></div>
            )}
            {isOwnProfile && (
              <button
                onClick={() => {
                  console.log('Edit profile photo');
                  setShowUploadModal(true);
                  // Set default tab to profile
                  setTimeout(() => {
                    const profileTab = document.querySelector('[data-tab="profile"]');
                    if (profileTab) profileTab.click();
                  }, 100);
                }}
                className="absolute bottom-2 right-2 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 shadow-lg"
                title="Edit profile photo"
              >
                <FaEdit className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-4xl mx-auto">

          {/* Basic Info */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {profile.full_name}
              {profile.is_vip && <FaCrown className="inline ml-2 text-yellow-500" />}
            </h1>
            <div className="flex items-center justify-center text-gray-600 mb-2">
              <FaMapMarkerAlt className="h-4 w-4 mr-1" />
              <span>{profile.city}, {profile.country}</span>
            </div>
            {profile.bio && (
              <p className="text-gray-700 max-w-md mx-auto">{profile.bio}</p>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
              <FaHeart className="h-6 w-6 text-pink-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{totalLikes}</div>
              <div className="text-sm text-gray-600">Likes</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
              <FaEye className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{visitors.length}</div>
              <div className="text-sm text-gray-600">Views</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
              <FaUserFriends className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{profileFriends.length}</div>
              <div className="text-sm text-gray-600">Friends</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('about')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors ${
                    activeTab === 'about'
                      ? 'border-b-2 border-pink-500 text-pink-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  About
                </button>
                <button
                  onClick={() => setActiveTab('photos')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors ${
                    activeTab === 'photos'
                      ? 'border-b-2 border-pink-500 text-pink-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Photos
                </button>
                <button
                  onClick={() => setActiveTab('friends')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors ${
                    activeTab === 'friends'
                      ? 'border-b-2 border-pink-500 text-pink-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Friends ({profileFriends.length})
                </button>
                <button
                  onClick={() => setActiveTab('visitors')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors ${
                    activeTab === 'visitors'
                      ? 'border-b-2 border-pink-500 text-pink-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Visitors ({visitors.length})
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'about' && (
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FaInfoCircle className="h-5 w-5 mr-2 text-pink-500" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {profile.height && (
                        <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">Height:</span>
                          <span className="ml-auto text-gray-900">{profile.height} cm</span>
                        </div>
                      )}
                      {profile.weight && (
                        <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">Weight:</span>
                          <span className="ml-auto text-gray-900">{profile.weight} kg</span>
                        </div>
                      )}
                      {profile.interests && (
                        <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700 block mb-2">Interests:</span>
                          <div className="flex flex-wrap gap-2">
                            {profile.interests.split(',').map((interest, index) => (
                              <span key={index} className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">
                                {interest.trim()}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Social Media */}
                  {(profile.instagram_username || profile.facebook_username || profile.telegram_username) && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaStar className="h-5 w-5 mr-2 text-pink-500" />
                        Social Media
                      </h3>
                      <div className="space-y-2">
                        {profile.instagram_username && (
                          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                            <FaInstagram className="h-5 w-5 text-pink-500 mr-3" />
                            <span className="text-gray-700">@{profile.instagram_username}</span>
                          </div>
                        )}
                        {profile.facebook_username && (
                          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                            <FaFacebook className="h-5 w-5 text-blue-500 mr-3" />
                            <span className="text-gray-700">{profile.facebook_username}</span>
                          </div>
                        )}
                        {profile.telegram_username && (
                          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                            <FaTelegram className="h-5 w-5 text-blue-400 mr-3" />
                            <span className="text-gray-700">@{profile.telegram_username}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* VIP Status */}
                  {profile.is_vip && (
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg p-4">
                      <div className="flex items-center">
                        <FaCrown className="h-6 w-6 text-white mr-3" />
                        <div>
                          <h4 className="text-white font-semibold">VIP Member</h4>
                          <p className="text-yellow-100 text-sm">Enjoy premium features and enhanced visibility</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'photos' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
                      <FaImages className="h-5 w-5 mr-2 text-pink-500" />
                      Photos
                    </h3>
                    {isOwnProfile && (
                      <button
                        onClick={() => {
                          console.log('🔘 Add Photo button clicked');
                          setShowUploadModal(true);
                        }}
                        className="bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors flex items-center"
                      >
                        <FaCamera className="h-4 w-4 mr-2" />
                        Add Photo
                      </button>
                    )}
                  </div>

                  {(normalPhotos.length === 0 && privatePhotos.length === 0) ? (
                    <div className="text-center py-12">
                      <FaCamera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-gray-500 font-medium">No photos yet</h4>
                      <p className="text-gray-400 text-sm mt-2">Add some photos to make your profile more attractive</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Public Photos */}
                      {normalPhotos.length > 0 && (
                        <div>
                          <h4 className="text-md font-medium text-gray-700 mb-3">Public Photos ({normalPhotos.length})</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {normalPhotos.map((photo, index) => (
                              <div key={`normal-${index}`} className="aspect-square rounded-lg overflow-hidden relative group">
                                <img
                                  src={getUploadUrl(photo.path)}
                                  alt={`Normal photo ${index + 1}`}
                                  className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                                />
                                {/* Approval Status Overlay */}
                                {photo.approval_status === 'pending' && (
                                  <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center">
                                    <div className="text-center text-white">
                                      <div className="text-sm font-medium mb-1">Onay Bekliyor</div>
                                      <div className="text-xs opacity-90">Admin incelemesi gerekli</div>
                                    </div>
                                  </div>
                                )}
                                {isOwnProfile && photo.approval_status !== 'pending' && (
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePhoto(photo.id, 'normal');
                                      }}
                                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                                      title="Delete photo"
                                    >
                                      <FaTrash className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Private Photos */}
                      {privatePhotos.length > 0 && (
                        <div>
                          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                            Private Photos ({privatePhotos.length})
                            {!isOwnProfile && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                Access Required
                              </span>
                            )}
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {privatePhotos.map((photo, index) => (
                              <div key={`private-${index}`} className="aspect-square rounded-lg overflow-hidden relative group">
                                <img
                                  src={getUploadUrl(photo.path)}
                                  alt={`Private photo ${index + 1}`}
                                  className={`w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer ${
                                    !isOwnProfile ? 'filter blur-sm' : ''
                                  }`}
                                />
                                {/* Approval Status Overlay - takes precedence over private photo overlay */}
                                {photo.approval_status === 'pending' && isOwnProfile && (
                                  <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center">
                                    <div className="text-center text-white">
                                      <div className="text-sm font-medium mb-1">Onay Bekliyor</div>
                                      <div className="text-xs opacity-90">Admin incelemesi gerekli</div>
                                    </div>
                                  </div>
                                )}
                                {!isOwnProfile && (
                                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                    <div className="text-center text-white">
                                      <FaShieldAlt className="h-8 w-8 mx-auto mb-2" />
                                      <p className="text-sm font-medium">Private Photo</p>
                                      <p className="text-xs">Request access to view</p>
                                    </div>
                                  </div>
                                )}
                                {isOwnProfile && photo.approval_status !== 'pending' && (
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePhoto(photo.id, 'private');
                                      }}
                                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                                      title="Delete photo"
                                    >
                                      <FaTrash className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          {!isOwnProfile && (
                            <div className="mt-4 text-center">
                              <button className="bg-pink-500 text-white px-6 py-2 rounded-lg hover:bg-pink-600 transition-colors text-sm">
                                Request Access to Private Photos
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'friends' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FaUserFriends className="h-5 w-5 mr-2 text-pink-500" />
                    Friends & Requests
                  </h3>

                  {/* Friends Sub-tabs */}
                  <div className="flex border-b border-gray-200 mb-6">
                    <button
                      onClick={() => setActiveFriendsTab('friends')}
                      className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                        activeFriendsTab === 'friends'
                          ? 'border-pink-500 text-pink-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Friends ({profileFriends.length})
                    </button>
                    <button
                      onClick={() => setActiveFriendsTab('sent')}
                      className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                        activeFriendsTab === 'sent'
                          ? 'border-pink-500 text-pink-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Sent Requests ({sentFriendRequests.length})
                    </button>
                    <button
                      onClick={() => setActiveFriendsTab('received')}
                      className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                        activeFriendsTab === 'received'
                          ? 'border-pink-500 text-pink-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Received Requests ({friendRequests.length})
                    </button>
                  </div>

                  {/* Friends Sub-tab Content */}
                  {activeFriendsTab === 'friends' && (
                    <div>
                      {profileFriends.length === 0 ? (
                        <div className="text-center py-8">
                          <FaUserFriends className="text-gray-300 text-4xl mx-auto mb-4" />
                          <p className="text-gray-500">No friends yet</p>
                          <p className="text-sm text-gray-400 mt-2">Start connecting with people!</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {profileFriends.map((friend, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                if (friend.id === user.id) {
                                  navigate(`/user/${friend.id}`);
                                } else {
                                  navigate(`/user/profile/${friend.id}`);
                                }
                              }}
                              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                            >
                              <div className="text-center">
                                  <img
                                    src={friend.profile_picture ? getUploadUrl(friend.profile_picture) : getProfilePicture(friend)}
                                    alt={friend.full_name}
                                    className="w-16 h-16 rounded-full mx-auto mb-3 object-cover"
                                  />
                                <h4 className="font-medium text-gray-900 text-sm truncate">{friend.full_name}</h4>
                                <p className="text-xs text-gray-500 mt-1">
                                  {friend.city && friend.country ? `${friend.city}, ${friend.country}` : 'Location not set'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeFriendsTab === 'sent' && (
                    <div>
                      {sentFriendRequests.length === 0 ? (
                        <div className="text-center py-8">
                          <FaUserFriends className="text-gray-300 text-4xl mx-auto mb-4" />
                          <p className="text-gray-500">No sent requests</p>
                          <p className="text-sm text-gray-400 mt-2">Your sent friend requests will appear here</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {sentFriendRequests.map((request) => (
                            <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <Link to={`/user/${request.id}`} className="flex items-center">
                                  <img
                                    src={getProfilePicture(request)}
                                    alt={request.full_name}
                                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm mr-3"
                                  />
                                  <div>
                                    <div className="font-medium text-gray-900">{request.full_name}</div>
                                    <div className="text-sm text-gray-500">
                                      {request.age && request.gender ? `${request.age} years old • ${request.gender}` : 'Age/Gender not specified'}
                                    </div>
                                  </div>
                                </Link>
                              </div>
                              <div className="text-sm text-gray-500">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeFriendsTab === 'received' && (
                    <div>
                      {friendRequests.length === 0 ? (
                        <div className="text-center py-8">
                          <FaUserFriends className="text-gray-300 text-4xl mx-auto mb-4" />
                          <p className="text-gray-500">No friend requests</p>
                          <p className="text-sm text-gray-400 mt-2">New requests will appear here</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {friendRequests.map((request) => (
                            <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <Link to={`/user/${request.id}`} className="flex items-center">
                                  <img
                                    src={getProfilePicture(request)}
                                    alt={request.full_name}
                                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm mr-3"
                                  />
                                  <div>
                                    <div className="font-medium text-gray-900">{request.full_name}</div>
                                    <div className="text-sm text-gray-500">
                                      {request.age && `${request.age} years old`}
                                      {request.age && request.city && ' • '}
                                      {request.city}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      Sent {new Date(request.time * 1000).toLocaleDateString()}
                                    </div>
                                  </div>
                                </Link>
                              </div>

                              <div className="flex space-x-2">
                                <button
                                  onClick={() => acceptFriendRequest(request.id)}
                                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center transition-colors"
                                >
                                  <FaCheck className="mr-2" />
                                  Accept
                                </button>
                                <button
                                  onClick={() => declineFriendRequest(request.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center transition-colors"
                                >
                                  <FaTimes className="mr-2" />
                                  Decline
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}

              {activeTab === 'visitors' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FaEye className="h-5 w-5 mr-2 text-pink-500" />
                    Profile Visitors ({visitors.length})
                  </h3>

                  {visitors.length === 0 ? (
                    <div className="text-center py-12">
                      <FaEye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-gray-500 font-medium">No visitors yet</h4>
                      <p className="text-gray-400 text-sm mt-2">People who viewed your profile will appear here</p>
                      <div className="text-xs text-gray-300 mt-4 space-y-1">
                        <p>Total profile visitors: {visitors.length}</p>
                        <p>Visitors shown: {visitors.length}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {visitors.map((visitor, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            if (visitor.id === user.id) {
                              navigate(`/user/${visitor.id}`);
                            } else {
                              navigate(`/user/profile/${visitor.id}`);
                            }
                          }}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="text-center">
                            <img
                              src={visitor.profile_picture ? getUploadUrl(visitor.profile_picture) : getProfilePicture(visitor)}
                              alt={visitor.full_name}
                              className="w-16 h-16 rounded-full mx-auto mb-3 object-cover"
                            />
                            <h4 className="font-medium text-gray-900 text-sm truncate">{visitor.full_name}</h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {visitor.city && visitor.country ? `${visitor.city}, ${visitor.country}` : 'Location not set'}
                            </p>
                            {visitor.is_online && (
                              <div className="flex items-center justify-center mt-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                                <span className="text-xs text-green-600">Online</span>
                              </div>
                            )}
                            <div className="text-xs text-gray-400 mt-2 space-y-1">
                              <p>Last visit: {visitor.view_time ? formatDate(visitor.view_time) : 'recently'}</p>
                             
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Quick Actions (only for own profile) */}
          {isOwnProfile && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => navigate('/vip')}
                  className="flex flex-col items-center p-4 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-colors"
                >
                  <FaCrown className="h-6 w-6 text-white mb-2" />
                  <span className="text-sm font-medium text-white">VIP</span>
                </button>
                <button
                  onClick={() => setActiveTab('visitors')}
                  className="flex flex-col items-center p-4 bg-gradient-to-br from-pink-400 to-pink-500 rounded-lg hover:from-pink-500 hover:to-pink-600 transition-colors"
                >
                  <FaEye className="h-6 w-6 text-white mb-2" />
                  <span className="text-sm font-medium text-white">Visitors</span>
                </button>
                <button
                  onClick={() => navigate('/credits')}
                  className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-400 to-blue-500 rounded-lg hover:from-blue-500 hover:to-blue-600 transition-colors"
                >
                  <FaCoins className="h-6 w-6 text-white mb-2" />
                  <span className="text-sm font-medium text-white">Credits</span>
                </button>
                <button
                  onClick={() => setActiveTab('friends')}
                  className="flex flex-col items-center p-4 bg-gradient-to-br from-green-400 to-green-500 rounded-lg hover:from-green-500 hover:to-green-600 transition-colors"
                >
                  <FaUserFriends className="h-6 w-6 text-white mb-2" />
                  <span className="text-sm font-medium text-white">Friends</span>
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={(success) => {
          console.log('📸 Photo upload completed, success:', success);
          if (success) {
            // Refresh only the photo data without full page reload
            console.log('🔄 Refreshing photo data after successful upload');
            const profileId = id || user.id;
            fetchProfile(profileId);
          }
        }}
        allowProfilePhoto={true}
        currentNormalPhotos={normalPhotos.length}
        currentPrivatePhotos={privatePhotos.length}
        currentProfilePhotos={profile?.profile_picture && profile.profile_picture !== 'default_avatar.png' ? 1 : 0}
        maxNormalPhotos={6}
        maxPrivatePhotos={6}
        maxProfilePhotos={1}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="text-center">
                <FaTrash className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Photo</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this {photoToDelete?.type === 'normal' ? 'public' : 'private'} photo? This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setPhotoToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeletePhoto}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;